import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingDialog from "@/components/BookingDialog";
import { useT } from "@/contexts/LanguageContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Truck, Weight, Euro, ArrowRight, ChevronDown, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RentalPage() {
  const t = useT();
  const { lang } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vehicles`);
      setVehicles(res.data);
    } catch {
      toast.error(t("rental_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const filteredVehicles = filter === "all" ? vehicles : vehicles.filter(v => v.vehicle_type === filter);

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDialogOpen(true);
  };

  const handleBookingComplete = () => {
    setDialogOpen(false);
    setSelectedVehicle(null);
    fetchVehicles();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="rental-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-20 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFD700]">{t("rental_overline")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white mt-2 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="rental-heading">
            {t("rental_heading")}
          </h1>
          <p className="text-base text-white/70 max-w-lg mb-8">{t("rental_sub")}</p>
          <div className="flex items-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#FFD700] rounded-full" />
              {t("rental_step_1")}
            </div>
            <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#FFD700]/60 rounded-full" />
              {t("rental_step_2")}
            </div>
            <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#FFD700]/30 rounded-full" />
              {t("rental_step_3")}
            </div>
          </div>
        </div>
      </section>

      {/* Filter & Fleet */}
      <section className="py-16" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10" data-testid="vehicle-filter">
            {[
              { value: "all", labelKey: "rental_filter_all" },
              { value: "lkw", labelKey: "rental_filter_lkw" },
              { value: "transporter", labelKey: "rental_filter_trans" },
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`text-sm font-medium px-4 py-2 rounded-sm border transition-colors ${
                  filter === f.value ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#475569] border-border hover:border-[#002FA7] hover:text-[#002FA7]"
                }`}
                data-testid={`filter-${f.value}`}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-[#475569]" data-testid="loading-state">{t("rental_loading")}</div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-20 text-[#475569]" data-testid="empty-state">{t("rental_empty")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVehicles.map((vehicle) => (
                <div key={vehicle.id} className="vehicle-card border border-border overflow-hidden group cursor-pointer" onClick={() => handleSelectVehicle(vehicle)} data-testid={`vehicle-card-${vehicle.id}`}>
                  <div className="relative h-52 overflow-hidden bg-[#F3F5F7]">
                    <img src={vehicle.image_url} alt={vehicle.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <Badge className="absolute top-3 left-3 bg-[#002FA7] text-white hover:bg-[#002FA7] rounded-sm text-xs">{vehicle.weight_class}</Badge>
                    {vehicle.available_from && new Date(vehicle.available_from) <= new Date() ? (
                      <Badge className="absolute top-3 right-3 bg-emerald-600 text-white hover:bg-emerald-600 rounded-sm text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {lang === "de" ? "Sofort verfügbar" : "Available now"}
                      </Badge>
                    ) : (
                      <Badge className="absolute top-3 right-3 bg-amber-500 text-white hover:bg-amber-500 rounded-sm text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {vehicle.status_label || (lang === "de" ? "Bald verfügbar" : "Coming soon")}
                      </Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{vehicle.name}</h3>
                      <div className="text-right shrink-0 ml-3">
                        <span className="text-xl font-black text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{vehicle.daily_rate.toFixed(0)}</span>
                        <span className="text-xs text-[#475569] ml-0.5">{t("rental_per_day")}</span>
                      </div>
                    </div>
                    <p className="text-sm text-[#475569] mb-4 line-clamp-2">{vehicle.description}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {vehicle.features.slice(0, 3).map((f) => (<span key={f} className="feature-tag">{f}</span>))}
                      {vehicle.features.length > 3 && (<span className="feature-tag">+{vehicle.features.length - 3}</span>)}
                    </div>
                    <Button className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-semibold rounded-sm h-10 text-sm" data-testid={`book-vehicle-${vehicle.id}`}>
                      {t("rental_book")}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Box */}
      <section className="py-12 bg-[#F3F5F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Truck, titleKey: "rental_info_1_title", descKey: "rental_info_1_desc" },
              { icon: Euro, titleKey: "rental_info_2_title", descKey: "rental_info_2_desc" },
              { icon: Weight, titleKey: "rental_info_3_title", descKey: "rental_info_3_desc" },
            ].map((item) => (
              <div key={item.titleKey} className="flex items-start gap-4 p-5 bg-white border border-border">
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#0A0A0A] mb-1">{t(item.titleKey)}</h4>
                  <p className="text-sm text-[#475569]">{t(item.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookingDialog vehicle={selectedVehicle} open={dialogOpen} onOpenChange={setDialogOpen} onComplete={handleBookingComplete} />
      <Footer />
    </div>
  );
}
